import { ReactNode } from "react";
import {
  DeleteSweepOutlined,
  FactCheckOutlined,
  PublishedWithChangesOutlined,
  SwapHorizOutlined,
} from "@mui/icons-material";
import { FormActions } from "../../components/common/FormActions";
import { IFormSection, TFormHookParams } from "../../types/common";
import { API_ASSETS, API_ASSETS_EXPORT } from "../../utils/apis";
import {
  CAN_DISPOSE_ASSETS,
  CAN_EDIT_ASSETS,
  CAN_PERFORM_ASSET_STOCK_COUNT,
  CAN_REGISTER_ASSETS,
  CAN_TRANSFER_ASSETS,
  CAN_UPDATE_ASSET_STATUS,
} from "../../utils/constants";
import {
  createDeleteAction,
  createViewEdit,
  formatDate,
  getForm,
  getRegister,
  objectWithUnsetKeys,
} from "../../utils/helpers";
import {
  URL_ASSET_DISPOSALS,
  URL_ASSET_STATUS_UPDATES,
  URL_ASSET_STOCK_COUNTS,
  URL_ASSET_TRANSFERS,
  URL_ASSETS,
} from "../../utils/urls";
import {
  ASSET_ASSIGNED,
  ASSET_AVAILABLE,
  ASSET_CONDITIONS,
  ASSET_STATUSES,
  ASSET_TYPES,
  AssetStatusTag,
  assetExport,
  auditFields,
  canTransferAsset,
  createdAtFilter,
  isDisposed,
  showNumber,
  toFilterOptions,
  toSelectItems,
  useAssetPermissions,
  useCategoryOptions,
  useDistrictOptions,
  userFullName,
  useUserOptions,
} from "./assetUtils";

// Link to one of the asset workflow forms with the asset preselected
const assetWorkflowAction = (
  label: string,
  url: string,
  assetId: string,
  icon: ReactNode,
) => ({
  label,
  icon,
  to: `${getForm(url)}?asset=${assetId}`,
});

export const useAssets = () => {
  const can = useAssetPermissions();

  const tableColumns = () => [
    { id: "asset_id", header: "Asset ID" },
    { id: "name", header: "Name", filter: { type: "text" } },
    {
      id: "category_name",
      header: "Category",
      filter: { type: "text", key: "category" },
    },
    {
      id: "asset_type",
      header: "Asset Type",
      filter: { type: "select", options: toFilterOptions(ASSET_TYPES) },
    },
    { id: "serial_number", header: "Serial Number" },
    { id: "condition", header: "Condition" },
    {
      id: "status",
      header: "Status",
      filter: { type: "select", options: toFilterOptions(ASSET_STATUSES) },
    },
    { id: "quantity", header: "Quantity" },
    {
      id: "location_name",
      header: "Location",
      filter: { type: "text", key: "location" },
    },
    { id: "assigned_to", header: "Assigned To" },
    { id: "created_at", header: "Registered On", filter: createdAtFilter },
    { id: "actions", header: "Actions" },
  ];

  const rowActions = (item: any) => [
    ...createViewEdit(URL_ASSETS, item?.id, can(CAN_EDIT_ASSETS)),
    ...(can(CAN_TRANSFER_ASSETS) && canTransferAsset(item)
      ? [
          assetWorkflowAction(
            "Transfer",
            URL_ASSET_TRANSFERS,
            item?.id,
            <SwapHorizOutlined fontSize="small" color="info" />,
          ),
        ]
      : []),
    ...(can(CAN_UPDATE_ASSET_STATUS) && !isDisposed(item)
      ? [
          assetWorkflowAction(
            "Update Status",
            URL_ASSET_STATUS_UPDATES,
            item?.id,
            <PublishedWithChangesOutlined fontSize="small" color="primary" />,
          ),
        ]
      : []),
    ...(can(CAN_PERFORM_ASSET_STOCK_COUNT) && !isDisposed(item)
      ? [
          assetWorkflowAction(
            "Stock Count",
            URL_ASSET_STOCK_COUNTS,
            item?.id,
            <FactCheckOutlined fontSize="small" color="success" />,
          ),
        ]
      : []),
    ...(can(CAN_DISPOSE_ASSETS) && !isDisposed(item)
      ? [
          assetWorkflowAction(
            "Dispose",
            URL_ASSET_DISPOSALS,
            item?.id,
            <DeleteSweepOutlined fontSize="small" color="warning" />,
          ),
        ]
      : []),
    ...(can(CAN_EDIT_ASSETS)
      ? [createDeleteAction(`${API_ASSETS}${item?.id}/`, item?.asset_id)]
      : []),
  ];

  const tableData = (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      asset_type: ASSET_TYPES[item?.asset_type] ?? item?.asset_type,
      condition: ASSET_CONDITIONS[item?.condition] ?? item?.condition,
      status: <AssetStatusTag status={item?.status} />,
      assigned_to: userFullName(item?.assigned_to_details),
      created_at: formatDate(item?.created_at),
      actions: <FormActions actions={rowActions(item)} />,
    }));

  const viewData = (item: any) => ({
    sections: [
      {
        title: "Asset Details",
        fields: {
          asset_id: item?.asset_id,
          name: item?.name,
          category: item?.category_name,
          asset_type: ASSET_TYPES[item?.asset_type] ?? item?.asset_type,
          serial_number: item?.serial_number,
          model_number: item?.model_number,
          purchase_date: item?.purchase_date,
          condition: ASSET_CONDITIONS[item?.condition] ?? item?.condition,
          status: <AssetStatusTag status={item?.status} />,
          quantity: showNumber(item?.quantity),
          location: item?.location_name,
          assigned_to: userFullName(item?.assigned_to_details),
        },
      },
      { title: "Record", fields: auditFields(item) },
    ],
  });

  return {
    tableTitle: "Asset",
    tableColumns,
    tableData,
    viewData,
    activityFilter: "assets",
    formRoute: can(CAN_REGISTER_ASSETS) ? getForm(URL_ASSETS) : undefined,
    createTitle: "Register Asset",
    exportData: assetExport(API_ASSETS_EXPORT, "asset_register"),
  };
};

export const useAssetsForm = ({ id, form }: TFormHookParams = {}) => {
  const isUpdate = !!id;
  const districts = useDistrictOptions();
  const categories = useCategoryOptions();
  const users = useUserOptions(form?.assigned_to_details);

  const isAssigned = form?.status === ASSET_ASSIGNED;

  const sections: IFormSection[] = [
    {
      title: "Asset Details",
      fields: [
        {
          field_name: "asset_id",
          label: "Asset ID",
          type: "text",
          visible: isUpdate,
          props: { disabled: true },
        },
        {
          field_name: "name",
          label: "Asset Name",
          type: "text",
          props: { required: !isUpdate, placeholder: "e.g. POS Machine 12" },
        },
        {
          field_name: "category",
          label: "Asset Category",
          type: "select",
          options_data: categories.options,
          loading: categories.loading,
          props: { required: !isUpdate, placeholder: "Select category" },
        },
        {
          field_name: "asset_type",
          label: "Asset Type",
          type: "select",
          options_data: toSelectItems(ASSET_TYPES),
          props: { required: !isUpdate, placeholder: "Select asset type" },
        },
        {
          field_name: "serial_number",
          label: "Serial Number",
          type: "text",
          // immutable once registered
          props: { required: !isUpdate, disabled: isUpdate },
        },
        {
          field_name: "model_number",
          label: "Model Number",
          type: "text",
        },
        {
          field_name: "purchase_date",
          label: "Purchase Date",
          type: "date",
          props: { required: !isUpdate, disableFuture: true },
        },
        {
          field_name: "condition",
          label: "Condition",
          type: "select",
          options_data: toSelectItems(ASSET_CONDITIONS),
          props: { required: !isUpdate, placeholder: "Select condition" },
        },
        {
          field_name: "quantity",
          label: "Quantity",
          type: "number",
          props: { required: !isUpdate, minValue: 1, showCommas: false },
        },
      ],
    },
    {
      title: "Location & Assignment",
      fields: [
        {
          field_name: "location",
          label: "Location",
          type: "select",
          options_data: districts.options,
          loading: districts.loading,
          props: { required: !isUpdate, placeholder: "Select location" },
        },
        {
          field_name: "status",
          label: "Status",
          type: "select",
          visible: !isUpdate,
          options_data: toSelectItems(ASSET_STATUSES),
          props: { required: true, placeholder: "Select status" },
        },
        {
          field_name: "assigned_to",
          label: "Assigned To",
          type: "select",
          search: users,
          props: {
            required: isAssigned && !isUpdate,
            allowClear: true,
            placeholder: "Search staff by first name",
          },
        },
      ],
    },
  ];

  const onSubmit = (payload: any) => {
    // server-owned/read-only fields
    const params = objectWithUnsetKeys(payload, [
      "asset_id",
      "category_name",
      "location_name",
      "assigned_to_details",
      "updated_by",
    ]);

    // serial_number is immutable once registered
    if (id) {
      return {
        id,
        api: API_ASSETS,
        params: objectWithUnsetKeys(params, ["serial_number"]),
      };
    }

    return {
      api: API_ASSETS,
      params: {
        ...params,
        assigned_to: params?.assigned_to || null,
      },
    };
  };

  return {
    sections,
    title: isUpdate ? "Edit Asset" : "Register Asset",
    redirect: getRegister(URL_ASSETS),
    onSubmit,
    defaultValues: isUpdate ? undefined : { status: ASSET_AVAILABLE },
  };
};
